import { useState, useCallback } from 'react';
import React from 'react';
import {
  RefreshCw,
  CheckSquare,
  Edit,
  AlertCircle,
  Filter,
  ChevronRight,
  Home,
  Database,
  Eye,
  FolderOpen,
  Image as ImageIcon,
} from 'lucide-react';
import { Button } from '@/components/ui';
import { useBrandfolderPhotos } from '../../hooks/useBrandfolderPhotos';
import { usePhotoUpload } from '../../hooks/usePhotoUpload';
import { LabelTree } from './LabelTree';
import { DamAssetGrid } from './DamAssetGrid';
import { DamAssetEditorModal, DamAssetUpload } from './DamAssetEditorModal';
import { DamAssetDetailModal } from './DamAssetDetailModal';
import { StatCard, StatGrid } from '../StatCard';
import { BrandfolderAsset, BreadcrumbItem } from '../../types/brandfolder';

interface DamPhotosTabProps {
  isSelectionMode: boolean;
  onToggleSelectionMode: () => void;
}

export const DamPhotosTab: React.FC<DamPhotosTabProps> = ({
  isSelectionMode,
  onToggleSelectionMode
}) => {
  const {
    currentLabelId,
    currentLabel,
    filteredAssets,
    loading,
    error,
    hasNextPage,
    filters,
    selectLabel,
    loadMore,
    refetch,
    setFilters
  } = useBrandfolderPhotos();

  const {
    uploadPhotos,
    progress: uploadProgress,
    error: uploadError,
    resetProgress
  } = usePhotoUpload();

  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set());
  const [showAssetEditor, setShowAssetEditor] = useState(false);
  const [previewAsset, setPreviewAsset] = useState<BrandfolderAsset | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);

  // Tree selection handler — receives labelId + full breadcrumb path from tree
  const handleTreeSelect = useCallback((labelId: string | undefined, crumbs: BreadcrumbItem[]) => {
    setBreadcrumbs(crumbs);
    selectLabel(labelId);
    setSelectedAssets(new Set()); // clear selection on navigation
  }, [selectLabel]);

  // Breadcrumb navigation — click a breadcrumb to jump to that label
  const handleBreadcrumbClick = useCallback((index: number) => {
    if (index < 0) {
      // Root
      setBreadcrumbs([]);
      selectLabel(undefined);
    } else {
      const target = breadcrumbs[index];
      setBreadcrumbs(breadcrumbs.slice(0, index + 1));
      selectLabel(target.label_id);
    }
    setSelectedAssets(new Set());
  }, [breadcrumbs, selectLabel]);

  const handleAssetSelect = useCallback((assetId: string) => {
    setSelectedAssets(prev => {
      const next = new Set(prev);
      if (next.has(assetId)) {
        next.delete(assetId);
      } else if (next.size < 5) {
        next.add(assetId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    const underReview = filteredAssets
      .filter(a => a.status === 'Under Review')
      .map(a => a.bf_asset_id)
      .slice(0, 5);
    setSelectedAssets(new Set(underReview));
  }, [filteredAssets]);

  const handleClearSelection = useCallback(() => {
    setSelectedAssets(new Set());
  }, []);

  const handleStatusFilter = useCallback((status: string) => {
    setFilters({
      status: status === '' ? undefined : status as 'Selected' | 'Under Review'
    });
  }, [setFilters]);

  const handleShowAssetEditor = useCallback(() => {
    setShowAssetEditor(true);
  }, []);

  const handleSaveEdited = useCallback(async (editedAssets: DamAssetUpload[]) => {
    setShowAssetEditor(false);

    const result = await uploadPhotos(
      editedAssets.map(a => a.id),
      editedAssets.map(a => ({
        id: a.id,
        title: a.title,
        description: a.description,
        source: 'Brandfolder' as const,
        type: a.type,
        village_id: a.village_id,
        program: a.program,
        asset_type: a.asset_type,
        date: a.date
      }))
    );

    if (result && result.success.length > 0) {
      refetch();
      const successIds = new Set(result.success.map(s => s.id));
      setSelectedAssets(prev => {
        const next = new Set(prev);
        successIds.forEach(id => next.delete(id));
        return next;
      });
    }
  }, [uploadPhotos, refetch]);

  const handlePreview = useCallback((asset: BrandfolderAsset) => {
    setPreviewAsset(asset);
  }, []);

  const handlePreviewNav = useCallback((direction: 'prev' | 'next') => {
    if (!previewAsset) return;
    const idx = filteredAssets.findIndex(a => a.bf_asset_id === previewAsset.bf_asset_id);
    if (idx === -1) return;
    const nextIdx = direction === 'next'
      ? (idx + 1) % filteredAssets.length
      : (idx - 1 + filteredAssets.length) % filteredAssets.length;
    setPreviewAsset(filteredAssets[nextIdx]);
  }, [previewAsset, filteredAssets]);

  const selectedAssetObjects = filteredAssets.filter(a => selectedAssets.has(a.bf_asset_id));

  return (
    <div className="space-y-4">
      {/* Stats specific to DAM data */}
      <StatGrid>
        <StatCard
          icon={ImageIcon}
          label="Total Assets"
          value={filteredAssets.length}
          variant="blue"
        />
        <StatCard
          icon={FolderOpen}
          label="Current Folder"
          value={currentLabel?.label_name || 'Root'}
          variant="green"
          compact
        />
        <StatCard
          icon={Eye}
          label="Status"
          value={filters.status || 'All'}
          variant="purple"
          compact
        />
        <StatCard
          icon={Database}
          label="Active Source"
          value="DAM Photos"
          variant="orange"
          compact
        />
      </StatGrid>

      {/* Breadcrumb bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-2">
        <div className="flex items-center flex-wrap gap-1 text-sm">
          <button
            onClick={() => handleBreadcrumbClick(-1)}
            className={`flex items-center px-2 py-1 rounded hover:bg-gray-100 transition-colors ${
              breadcrumbs.length === 0 ? 'text-green-700 font-medium' : 'text-gray-600'
            }`}
          >
            <Home className="h-3.5 w-3.5 mr-1" />
            Root
          </button>
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.label_id}>
              <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
              <button
                onClick={() => handleBreadcrumbClick(index)}
                className={`px-2 py-1 rounded hover:bg-gray-100 transition-colors truncate max-w-[180px] ${
                  index === breadcrumbs.length - 1
                    ? 'text-green-700 font-medium'
                    : 'text-gray-600'
                }`}
                title={crumb.label_name}
              >
                {crumb.label_name}
              </button>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Top bar with filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              value={filters.status || ''}
              onChange={(e) => handleStatusFilter(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="">All Statuses</option>
              <option value="Under Review">Under Review</option>
              <option value="Selected">Selected</option>
            </select>
            <span className="text-sm text-gray-500">
              {filteredAssets.length} asset{filteredAssets.length !== 1 ? 's' : ''}
              {currentLabel?.label_name && (
                <> in <span className="font-medium">{currentLabel.label_name}</span></>
              )}
            </span>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant={isSelectionMode ? "primary" : "secondary"}
              size="sm"
              onClick={onToggleSelectionMode}
            >
              <CheckSquare className="h-4 w-4 mr-1" />
              {isSelectionMode ? 'Exit Selection' : 'Select'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={refetch}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* Bulk Actions Panel */}
      {isSelectionMode && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-900">
                {selectedAssets.size}/5 selected
              </span>
              {selectedAssets.size >= 5 && (
                <span className="text-xs text-amber-600 font-medium">Maximum limit reached</span>
              )}
              <div className="flex items-center space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleSelectAll}
                  disabled={selectedAssets.size >= 5}
                >
                  Select First 5
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleClearSelection}
                  disabled={selectedAssets.size === 0}
                >
                  Clear
                </Button>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="secondary"
                onClick={handleShowAssetEditor}
                disabled={selectedAssets.size === 0 || uploadProgress.isUploading}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Metadata{selectedAssets.size > 0 ? ` (${selectedAssets.size})` : ''}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Status */}
      {uploadError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Upload Failed</h3>
              <p className="mt-1 text-sm text-red-700">{uploadError}</p>
              <Button variant="secondary" size="sm" onClick={resetProgress} className="mt-2">
                Dismiss
              </Button>
            </div>
          </div>
        </div>
      )}

      {!uploadProgress.isUploading && uploadProgress.total > 0 && !uploadError && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex">
            <CheckSquare className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">Upload Complete</h3>
              <p className="mt-1 text-sm text-green-700">
                Successfully uploaded {uploadProgress.completed} of {uploadProgress.total} assets.
                {uploadProgress.failed > 0 && (
                  <span className="block mt-1">{uploadProgress.failed} failed.</span>
                )}
              </p>
              <Button variant="secondary" size="sm" onClick={resetProgress} className="mt-2">
                Dismiss
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading Brandfolder data</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <Button variant="secondary" size="sm" onClick={refetch} className="mt-2">
                Try Again
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content: Tree + Grid */}
      <div className="flex gap-4">
        {/* Left sidebar - Label Tree */}
        <div className="w-72 flex-shrink-0 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" style={{ minHeight: '400px' }}>
          <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-medium text-gray-700">Labels</h3>
          </div>
          <LabelTree
            selectedLabelId={currentLabelId}
            onSelect={handleTreeSelect}
          />
        </div>

        {/* Right content - Asset Grid */}
        <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <DamAssetGrid
            assets={filteredAssets}
            loading={loading}
            hasNextPage={hasNextPage}
            onLoadMore={loadMore}
            selectedAssets={selectedAssets}
            isSelectionMode={isSelectionMode}
            onSelect={handleAssetSelect}
            onPreview={handlePreview}
            selectionLimit={5}
          />
        </div>
      </div>

      {/* DAM Asset Editor Modal */}
      {showAssetEditor && selectedAssetObjects.length > 0 && (
        <DamAssetEditorModal
          assets={selectedAssetObjects}
          onSave={handleSaveEdited}
          onClose={() => setShowAssetEditor(false)}
        />
      )}

      {/* DAM Asset Detail Modal */}
      {previewAsset && (
        <DamAssetDetailModal
          asset={previewAsset}
          onClose={() => setPreviewAsset(null)}
          onPrevious={filteredAssets.length > 1 ? () => handlePreviewNav('prev') : undefined}
          onNext={filteredAssets.length > 1 ? () => handlePreviewNav('next') : undefined}
        />
      )}
    </div>
  );
};
